import { Component, OnInit, NgZone } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../../../../environments/environment';
import { AuthservicesService } from '../../../../services/authservices.service';

declare const google: any;

@Component({
  selector: 'app-create-housing',
  templateUrl: './create-housing.component.html',
  styleUrls: ['./create-housing.component.css']
})
export class CreateHousingComponent implements OnInit {

  form: FormGroup;
  submitted = false;
  selectedImages: string[] = [];
  newPet: string = '';

  sellStatusOptions = ['Sale', 'Rent', 'Purchase'];
  propertyTypeOptions = [
    'Apartment/Flat', 'House', 'Condo', 'Townhouse', 'Duplex',
    'Guest House', 'Manufactured', 'Assisted Living', 'Farm/Ranch', 'Land', 'Commercial'
  ];

  housingTypeOptions = ['Private', 'Shared', ''];

  statusOptions = ['Active', 'Inactive', 'Sold', 'Rented', 'Purchased', 'Cancelled'];

  baseUrl = environment.imgUrl;

  parkingOptions = ['Yes', 'No', 'Garage', 'Street', 'Covered'];
  laundryOptions = ['Yes', 'No', 'In Unit', 'On Site', 'Hookups'];
  flooringOptions = ['Carpet', 'Hardwood', 'Tile', 'Vinyl', 'Laminate'];
  furnishedOptions = ['Yes', 'No', 'Partially'];
  smokingOptions = ['Yes', 'No', 'Outside Only'];
  amenitiesOptions = ['Pool', 'Gym', 'Parking', 'Security', 'Elevator', 'Balcony', 'Garden', 'Other'];
  evChargingOptions = ['Yes', 'No', 'Available'];
  wheelchairOptions = ['Yes', 'No', 'Partial'];
  petsOptions = ['No', 'Dogs', 'Cats', 'Birds', 'Other'];
  isEditMode = false;
  editData: any;


  selectedPetType: string = '';

  constructor(
    private fb: FormBuilder,
    private authService: AuthservicesService,
    private modalService: NgbModal,
    private router: Router,
    private zone: NgZone,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {

    this.form = this.fb.group({
      sellStatus: ['', Validators.required],
      propertyType: ['', Validators.required],
      housingType: [''],
      roomTitle: ['', [Validators.required, Validators.maxLength(80)]],

      bedRoomCount: [0],
      washRoomCount: [0],
      propertyArea: [0],
      propertyUnitArea: ['sq ft'],

      lotLandArea: [0],
      lotUnitArea: ['sq ft'],

      status: ['Active'],

      price: [''],
      monthlyRent: [0],
      weeklyRent: [0],
      rentalPeriod: [''],
      availableDate: [''],

      address: this.fb.group({
        googleAddress: [''],
        streetAddress: [''],
        landmark: [''],
        city: [''],
        state: [''],
        postalCode: [''],
        country: [''],
        contactNo: [''],
        alternateNo: [''],
        location: [null]
      }, { validators: this.addressRequired }),

      contactNumber: [''],
      email: [''],
      name: [''],
      contactInstruction: [''],

      parking: this.fb.array([]),
      laundry: this.fb.array([]),
      flooring: this.fb.array([]),
      furnished: this.fb.array([]),
      smoking: this.fb.array([]),
      amenities: this.fb.array([]),
      evCharging: this.fb.array([]),
      wheelchairAccessible: this.fb.array([]),

      pets: this.fb.array([]),

      duration: [0],
      openHouseInfo: [''],
      description: [''],
      isAvailable: [true],
      isShowProfile: [false],
      isFavorite: [false],

      images: this.fb.array([])
    });

    this.form.get('sellStatus')?.valueChanges.subscribe(val => {
      this.updatePriceFields(val);
    });

    this.updatePriceFields(this.form.get('sellStatus')?.value);

    const nav = this.router.getCurrentNavigation();
    this.editData = nav?.extras?.state?.['house'];

    if (!this.editData) {
      this.editData = history.state?.house;
    }

    if (this.editData) {
      this.isEditMode = true;
      this.patchForm(this.editData);
    }
  }
  patchForm(data: any) {

    this.form.patchValue({
      sellStatus: data.sellStatus,
      propertyType: data.propertyType,
      housingType: data.housingType,
      roomTitle: data.roomTitle,

      bedRoomCount: data.bedRoomCount,
      washRoomCount: data.washRoomCount,
      propertyArea: data.propertyArea,
      propertyUnitArea: data.propertyUnitArea,

      lotLandArea: data.lotLandArea,
      lotUnitArea: data.lotUnitArea,

      status: data.status,

      price: data.price,
      monthlyRent: data.monthlyRent,
      weeklyRent: data.weeklyRent,
      rentalPeriod: data.rentalPeriod,
      availableDate: data.availableDate ? data.availableDate.split('T')[0] : '',

      contactNumber: data.contactNumber,
      alternateNo: data.alternateNo,
      email: data.email,
      name: data.name,
      contactInstruction: data.contactInstruction,

      duration: data.duration,
      openHouseInfo: data.openHouseInfo,
      description: data.description,

      isAvailable: data.isAvailable,
      isShowProfile: data.isShowProfile,
      isFavorite: data.isFavorite
    });

    // Address
    this.form.get('address')?.patchValue({
      googleAddress: data.address?.googleAddress,
      streetAddress: data.address?.streetAddress,
      landmark: data.address?.landMark,
      city: data.address?.city,
      state: data.address?.state,
      postalCode: data.address?.postalCode,
      country: data.address?.country,
      contactNo: data.address?.contactNo,
      location: data.address?.location,
      alternateNo: data.address?.alternateNo
    });

    // FormArrays
    this.setArray('parking', data.additionalInformation?.Parking);
    this.setArray('laundry', data.additionalInformation?.Laundry);
    this.setArray('flooring', data.additionalInformation?.Flooring);
    this.setArray('furnished', data.additionalInformation?.Furnished);
    this.setArray('smoking', data.additionalInformation?.Smoking);
    this.setArray('amenities', data.additionalInformation?.Amenities);
    this.setArray('evCharging', data.additionalInformation?.EVCharging);
    this.setArray('wheelchairAccessible', data.additionalInformation?.WheelchairAccessible);

    //  Pets
    if (data.pets) {
      data.pets.forEach((p: string) => {
        this.petsArray.push(this.fb.control(p));
      });
    }

    //  Images
    if (data.images) {
      data.images.forEach((img: string) => {
        this.selectedImages.push(img);
        this.imagesArray.push(this.fb.control(img));
      });
    }
  }
  setArray(controlName: string, values: string[]) {
    if (!values) return;

    const arr = this.form.get(controlName) as FormArray;
    values.forEach(v => arr.push(this.fb.control(v)));
  }
  get f() { return this.form.controls; }
  get addr() { return this.form.get('address') as FormGroup; }

  get petsArray() { return this.form.get('pets') as FormArray; }
  get imagesArray() { return this.form.get('images') as FormArray; }

  private addressRequired(g: FormGroup) {
    const hasValue = Object.values(g.controls).some(c => !!c.value);
    return hasValue ? null : { addressRequired: true };
  }

  private updatePriceFields(sellStatus?: string) {
    const price = this.f.price;
    const monthly = this.f.monthlyRent;
    const weekly = this.f.weeklyRent;

    price?.clearValidators();
    monthly?.clearValidators();
    weekly?.clearValidators();

    if (sellStatus === 'Sale' || sellStatus === 'Lease') {
      price?.setValidators(Validators.required);
    } else if (sellStatus === 'Rent') {
      monthly?.setValidators(Validators.min(0));
      weekly?.setValidators(Validators.min(0));
    }

    price?.updateValueAndValidity({ emitEvent: false });
    monthly?.updateValueAndValidity({ emitEvent: false });
    weekly?.updateValueAndValidity({ emitEvent: false });
  }

  // Google Places
  AddressChange(place: google.maps.places.PlaceResult) {
    this.zone.run(() => {
      const a: any = {
        googleAddress: place.formatted_address || place.name || '',
        streetAddress: place.formatted_address || '',
        location: null
      };

      const lat = place.geometry?.location?.lat();
      const lng = place.geometry?.location?.lng();
      if (lat && lng) a.location = { type: 'Point', coordinates: [lng, lat] };

      for (const comp of place.address_components || []) {
        const t = comp.types[0];
        if (t === 'locality' || t === 'administrative_area_level_2') a.city = comp.long_name;
        if (t === 'administrative_area_level_1') a.state = comp.long_name;
        if (t === 'country') a.country = comp.long_name;
        if (t === 'postal_code') a.postalCode = comp.long_name;
      }

      this.form.patchValue({ address: a });
    });
  }

  toggleCheckbox(controlName: string, value: string, checked: boolean) {
    const arr = this.form.get(controlName) as FormArray;
    if (checked) {
      if (!arr.value.includes(value)) arr.push(this.fb.control(value));
    } else {
      const idx = arr.value.indexOf(value);
      if (idx > -1) arr.removeAt(idx);
    }
  }

  isChecked(controlName: string, value: string): boolean {
    return (this.form.get(controlName) as FormArray).value.includes(value);
  }

  // Pets
  addPet() {
    if (!this.selectedPetType || this.selectedPetType === 'Select Pet Type') {
      return;
    }
    const alreadyAdded = this.petsArray.value.some((p: string) => p === this.selectedPetType);
    if (!alreadyAdded) {
      this.petsArray.push(this.fb.control(this.selectedPetType));
    }
    this.selectedPetType = 'Select Pet Type';
  }

  removePet(index: number) {
    this.petsArray.removeAt(index);
  }

  onSubmit() {
    this.submitted = true;
    if (this.form.invalid) return;

    const val = this.form.value;

    const additionalInfo = {
      Parking: val.parking || [],
      Laundry: val.laundry || [],
      Flooring: val.flooring || [],
      Furnished: val.furnished || [],
      Smoking: val.smoking || [],
      Amenities: val.amenities || [],
      EVCharging: val.evCharging || [],
      WheelchairAccessible: val.wheelchairAccessible || []
    };

    const payload = {
      sellStatus: val.sellStatus,
      propertyType: val.propertyType,
      housingType: val.housingType || null,
      roomTitle: val.roomTitle?.trim() || '',

      bedRoomCount: Number(val.bedRoomCount) || 0,
      washRoomCount: Number(val.washRoomCount) || 0,
      propertyArea: Number(val.propertyArea) || 0,
      propertyUnitArea: val.propertyUnitArea || 'sq ft',

      lotLandArea: Number(val.lotLandArea) || 0,
      lotUnitArea: val.lotUnitArea || 'sq ft',

      price: val.price?.trim() || '',
      monthlyRent: Number(val.monthlyRent) || 0,
      weeklyRent: Number(val.weeklyRent) || 0,
      rentalPeriod: val.rentalPeriod?.trim() || null,
      duration: Number(val.duration) || 0,

      availableDate: val.availableDate ? new Date(val.availableDate) : null,
      openHouseInfo: val.openHouseInfo?.trim() || null,
      description: val.description?.trim() || '',

      status: val.status || 'Active',
      isAvailable: !!val.isAvailable,
      isShowProfile: !!val.isShowProfile,
      isFavorite: !!val.isFavorite,

      contactNumber: val.contactNumber?.trim() || '',
      // alternateNo: val.alternateNo?.trim() || '',
      email: val.email?.trim().toLowerCase() || '',
      name: val.name?.trim() || '',
      contactInstruction: val.contactInstruction?.trim() || '',

      pets: val.pets || [],

      images: val.images || [],
      defaultImage: -1,

      address: {
        googleAddress: val.address.googleAddress?.trim() || null,
        streetAddress: val.address.streetAddress?.trim() || null,
        landMark: val.address.landmark?.trim() || null,
        city: val.address.city?.trim() || null,
        state: val.address.state?.trim() || null,
        postalCode: val.address.postalCode?.trim() || null,
        country: val.address.country?.trim() || null,
        contactNo: val.address.contactNo?.trim() || null,
        alternateNo: val.address.alternateNo?.trim() || null,
        location: val.address.location || { type: 'Point', coordinates: [0, 0] }
      },

      additionalInformation: additionalInfo,

      ravelId: '',
      city: val.address.city?.trim() || '',
      postalCode: val.address.postalCode?.trim() || ''
    };

    if (Object.values(additionalInfo).every(arr => arr.length === 0)) {
    }

    if (this.isEditMode) {

      payload['housingPostId'] = this.editData?._id || this.editData?.id;

      console.log('UPDATE payload:', payload);

      this.authService.updateHousingPost(payload).subscribe({
        next: () => {
          this.authService.successMessage?.('Property updated', 'success');
          this.router.navigate(['/housing-hub']);
        }
      });

    } else {
      console.log('CREATE payload:', payload);

      this.authService.createHousingListing(payload).subscribe({
        next: () => {
          this.authService.successMessage?.('Property created', 'success');
          this.router.navigate(['/housing-hub']);
        }
      });
    }
  }

  open(modal: any) {
    this.modalService.open(modal, { centered: true });
  }

  addExampleImages() {
    const urls = [
      'https://via.placeholder.com/640x480?text=Property+1',
      'https://via.placeholder.com/640x480?text=Property+2'
    ];
    urls.forEach(u => {
      if (!this.selectedImages.includes(u)) {
        this.selectedImages.push(u);
        this.imagesArray.push(this.fb.control(u));
      }
    });
  }

  removeImage(i: number) {
    this.selectedImages.splice(i, 1);
    this.imagesArray.removeAt(i);
  }
}